/* -------------------------- Design imports start -------------------------- */
import { AddRounded, DeleteRounded, EditRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type MouseEvent, type ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import LogTool from '../../../logger/logTools'
import { type KeysOfFieldObject } from '../../Form'
import {
  cleanUpFormFields,
  generateAddFormFieldButton,
  generateAddSectionTitleButton,
  generateTextField,
} from '../utils/functions'
import { type EditorFieldObject } from '../utils/types'
import { useCreateFeedbackForm } from '../hooks/useFormEditorMutations'
import { inputToAPIFeedbackFormJSON } from '../../Item/api/articleApi'
import useUniqueKey from '../../../hooks/useUniqueKey'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formTitleSuggestion?: string
  formFieldSuggestion?: EditorFieldObject[]
  onClose?: () => void
  onConfirm?: (input: any) => Promise<'SUCCESS' | 'ERROR'> // Optional for backward compatibility
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function FormEditorDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'FormEditorDrawer', enable: true, logLevel: 'debug' })
  const uniqueFieldKey = useUniqueKey('formEditorInputField')
  const { t } = useTranslation()
  const unnamedFieldLabel = t('common:content.label.unnamedField')
  const createFeedbackFormMutation = useCreateFeedbackForm()
  const {
    open,
    setOpen,
    // formState,
    formTitleSuggestion = t('common:content.label.feedbackForm'),
    formFieldSuggestion = [
      generateTextField(uniqueFieldKey.get(), t('common:content.label.unnamedField')),
      generateAddFormFieldButton(),
      generateAddSectionTitleButton(),
    ],
    onClose = () => null,
    onConfirm,
  } = props
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [openFieldObjMenu, setOpenFieldObjMenu] = useState(false)
  const [pendingAnchorKey, setPendingAnchorKey] = useState<string | number | null>(null)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const fieldRefs = useRef<Record<string | number, HTMLElement | null>>({})
  const labelInputRef = useRef<HTMLInputElement | null>(null)
  const [formJson, setFormJson] = useState<{ formTitle: string; formFields: EditorFieldObject[] }>({
    formTitle: formTitleSuggestion,
    formFields: formFieldSuggestion,
  })
  const [selectedFormObj, setSelectedFormObj] = useState<EditorFieldObject>()
  const [formFieldMenuAnchor, setFormFieldMenuAnchor] = useState<null | HTMLElement>(null)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */

  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  const normalizeLabel = (label: string) =>
    label.trim().replace(/\s+/g, '_').replace(/\./g, '').toLowerCase()

  const hasDuplicateLabels = (fields: EditorFieldObject[]): boolean => {
    const labels = new Set<string>()
    return fields.some(field => {
      if (field.type === 'addFormField') return false
      if (field.type === 'addSectionTitle') return false
      const normalized = normalizeLabel(field.label)
      if (labels.has(normalized)) return true
      labels.add(normalized)
      return false
    })
  }

  const hasEmptyLabels = (fields: EditorFieldObject[]): boolean =>
    fields.some(
      field =>
        field.type !== 'addFormField' &&
        field.type !== 'addSectionTitle' &&
        field.label.trim() === ''
    )

  const getExistingLabels = (fields: EditorFieldObject[]): string[] =>
    fields.reduce<string[]>((acc, field) => {
      if (
        field.type !== 'addFormField' &&
        field.type !== 'addSectionTitle' &&
        field.label.trim() !== ''
      ) {
        acc.push(field.label)
      }
      return acc
    }, [])

  function buildNextLabel(baseLabel: string, existingLabels: string[], separator = ' '): string {
    const normalizedBase = normalizeLabel(baseLabel)
    let maxSuffix = 0

    existingLabels.forEach(label => {
      const normalized = normalizeLabel(label)
      if (normalized === normalizedBase) {
        maxSuffix = Math.max(maxSuffix, 1)
      } else if (normalized.startsWith(`${normalizedBase}_`)) {
        const suffix = Number.parseInt(normalized.slice(normalizedBase.length + 1), 10)
        if (!Number.isNaN(suffix)) {
          maxSuffix = Math.max(maxSuffix, suffix)
        }
      }
    })

    if (maxSuffix === 0) return baseLabel
    return `${baseLabel}${separator}${maxSuffix + 1}`
  }

  const isDuplicateLabel = (label: string, ignoreKey?: string | number | symbol): boolean => {
    if (!label) return false
    const normalizedLabel = normalizeLabel(label)
    return formJson.formFields.some(
      field =>
        field.type !== 'addFormField' &&
        field.type !== 'addSectionTitle' &&
        field.key !== ignoreKey &&
        normalizeLabel(field.label) === normalizedLabel
    )
  }

  const getLabelPlaceholder = (fields: EditorFieldObject[]): string =>
    buildNextLabel(unnamedFieldLabel, getExistingLabels(fields))

  const isEditableField = (
    field?: EditorFieldObject
  ): field is Exclude<EditorFieldObject, { type: 'addFormField' | 'addSectionTitle' }> =>
    Boolean(field && field.type !== 'addFormField' && field.type !== 'addSectionTitle')

  function getFieldObjIndex(formField: EditorFieldObject): number {
    return formJson.formFields.indexOf(formField)
  }

  /**
   * Helper function that updates the selectedFormObj and formJson at once. This
   * makes the formFieldMenu interactive: the edited formField changes in real time.
   * @param prop The formField property to update.
   * @param value The value to apply to the formField property.
   */
  function updateFieldObj(fieldObj: EditorFieldObject, prop: KeysOfFieldObject, value: any) {
    // get the index of the selectedFormObj before updating the porperty
    const i = getFieldObjIndex(fieldObj)

    // get an un-typed copy of the formField. This enables us to add any prop to it
    const updatedFormField = {
      ...fieldObj,
    } as { [key: string]: any }

    // update the property
    updatedFormField[prop] = value

    setSelectedFormObj(updatedFormField as EditorFieldObject)
    setFormJson(prev => {
      prev.formFields[i] = updatedFormField as EditorFieldObject
      return {
        ...prev,
        formFields: prev.formFields,
      }
    })
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose?: boolean) => {
    // reset FormEditor
    uniqueFieldKey.reset()
    setFormJson({
      formTitle: formTitleSuggestion,
      formFields: formFieldSuggestion,
    })
    setSelectedFormObj(undefined)
    setFormFieldMenuAnchor(null)
    setOpenFieldObjMenu(false)

    !preventOnClose && onClose()
  }

  const handleConfirm = async () => {
    if (hasEmptyLabels(formJson.formFields)) {
      toast.error(t('common:feedback.error.missingFieldLabel'))
      return
    }

    if (hasDuplicateLabels(formJson.formFields)) {
      toast.error(t('common:feedback.error.duplicateFieldLabel'))
      return
    }

    try {
      // Prepare form data
      const formData = {
        formTitle: formJson.formTitle,
        formJson: cleanUpFormFields(formJson.formFields),
      }

      // Convert to API format
      const apiFormData = inputToAPIFeedbackFormJSON(formData)

      // Use the mutation hook if available, otherwise fall back to onConfirm prop for backward compatibility
      if (onConfirm) {
        // Legacy path: use onConfirm prop
        const confirmStatus = await onConfirm(formData)
        if (confirmStatus === 'SUCCESS') {
          toast.success(t('common:feedback.success.creatingFeedbackForm'))
          handleClose(true)
        } else {
          toast.error(t('common:feedback.error.creatingFeedbackForm'))
        }
      } else {
        // New path: use React Query mutation
        await createFeedbackFormMutation.mutateAsync(apiFormData)
        toast.success(t('common:feedback.success.creatingFeedbackForm'))
        handleClose(true)
      }
    } catch (error: any) {
      log.error('Error creating feedback form:', error)
      toast.error(t('common:feedback.error.creatingFeedbackForm'))
    }
  }

  const handleAddFormField = (_event: MouseEvent<HTMLButtonElement>) => {
    if (hasEmptyLabels(formJson.formFields)) {
      toast.error(t('common:feedback.error.missingFieldLabel'))
      return
    }

    if (hasDuplicateLabels(formJson.formFields)) {
      toast.error(t('common:feedback.error.duplicateFieldLabel'))
      return
    }

    const newFieldKey = uniqueFieldKey.get()
    const existingLabels = getExistingLabels(formJson.formFields)
    const newLabel = buildNextLabel(unnamedFieldLabel, existingLabels)
    const createdField: EditorFieldObject = {
      key: newFieldKey,
      label: newLabel,
      type: 'text',
      value: null,
      onChange: () => null,
      required: false,
    }
    setFormJson(prev => {
      // get a copy of the current form fields
      const updatedFormFields = [...prev.formFields]
      // separate the add buttons
      const buttonFormObjects = updatedFormFields.splice(updatedFormFields.length - 2)
      // stitch everything together
      return {
        ...prev,
        formFields: [...updatedFormFields, createdField, ...buttonFormObjects],
      }
    })

    setSelectedFormObj(createdField)
    setPendingAnchorKey(newFieldKey)
    setOpenFieldObjMenu(false)
  }

  const handleAddSectionTitle = () => {
    const newFieldKey = uniqueFieldKey.get()
    const createdField: EditorFieldObject = {
      key: newFieldKey,
      type: 'sectionTitle',
      value: undefined,
      label: 'just another title',
    }
    setFormJson(prev => {
      // get a copy of the current form fields
      const updatedFormFields = [...prev.formFields]
      // separate the add buttons
      const buttonFormObjects = updatedFormFields.splice(updatedFormFields.length - 2)
      // stitch everything together
      return {
        ...prev,
        formFields: [...updatedFormFields, createdField, ...buttonFormObjects],
      }
    })

    setSelectedFormObj(createdField)
    setPendingAnchorKey(newFieldKey)
    setOpenFieldObjMenu(false)
  }

  const handleDeleteFormField = (formField: EditorFieldObject) => {
    // get a copy of the current formJson as so that we do not have to work on an array stored in a state variable
    const updatedFormFields = [...formJson.formFields]

    const i = getFieldObjIndex(formField)

    // delete formField
    updatedFormFields.splice(i, 1)
    setFormJson(prev => ({ ...prev, formFields: updatedFormFields }))
  }

  const handleOpenFieldObjMenu = (event: any, formField: EditorFieldObject) => {
    // prepare states to render the formFieldMenu with the correct context
    setSelectedFormObj(formField)
    const anchor =
      isEditableField(formField) && formField.key in fieldRefs.current
        ? fieldRefs.current[formField.key]
        : event.currentTarget
    setFormFieldMenuAnchor(anchor)

    // render formFieldMenu
    setOpenFieldObjMenu(true)
  }

  const handleCloseFieldObjMenu = () => {
    if (!isEditableField(selectedFormObj)) {
      setOpenFieldObjMenu(false)
      return
    }

    if (selectedFormObj.label.trim() === '') {
      toast.error(t('common:feedback.error.missingFieldLabel'))
      return
    }

    if (isDuplicateLabel(selectedFormObj.label, selectedFormObj.key)) {
      toast.error(t('common:feedback.error.duplicateFieldLabel'))
      return
    }

    setOpenFieldObjMenu(false)
  }

  const handleChangeFieldObjInputLength = (event: any) => {
    if (!isEditableField(selectedFormObj)) return
    updateFieldObj(selectedFormObj, 'type', event.target.value)
  }

  const handleChangeFieldObjLabel = (event: any) => {
    if (!isEditableField(selectedFormObj)) return
    log.debug('label ->', event.target.value)
    updateFieldObj(selectedFormObj, 'label', event.target.value)
  }

  const handleChangeFieldObjIsRequired = (event: any) => {
    if (!isEditableField(selectedFormObj)) return
    updateFieldObj(selectedFormObj, 'required', event.target.checked)
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const setFieldRef = (key: string | number | undefined) => (el: HTMLElement | null) => {
    if (typeof key !== 'undefined') {
      fieldRefs.current[key] = el
    }
  }

  const renderEditorFieldObject = (fieldObj: EditorFieldObject): React.ReactNode => {
    const fieldType = fieldObj.type
    switch (fieldType) {
      case 'addFormField':
        return (
          <Button
            startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
            onClick={handleAddFormField}
            sx={{ textTransform: 'none', fontSize: '16px', color: 'black', ml: '4px' }}
          >
            {t('common:interaction.button.addTextField')}
          </Button>
        )
      case 'addSectionTitle':
        return (
          <Button
            startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
            onClick={handleAddSectionTitle}
            sx={{ textTransform: 'none', fontSize: '16px', color: 'black', ml: '4px' }}
          >
            {t('common:interaction.button.addSectionTitle')}
          </Button>
        )
      case 'text':
        return (
          <Box display="flex" alignItems="center" ref={setFieldRef(fieldObj.key)}>
            <Stack
              direction="row"
              sx={{ height: '40px', mr: '0.2rem' }} // height needs to be set otherwise the iconbuttons become oval
            >
              <IconButton onClick={() => handleDeleteFormField(fieldObj)}>
                <DeleteRounded />
              </IconButton>
              <IconButton onClick={e => handleOpenFieldObjMenu(e, fieldObj)}>
                <EditRounded />
              </IconButton>
            </Stack>
            <TextField fullWidth label={fieldObj.label} required={fieldObj.required} />
          </Box>
        )
      case 'multiline':
        return (
          <Box display="flex" alignItems="start" ref={setFieldRef(fieldObj.key)}>
            <Stack
              direction="row"
              sx={{ height: '40px', mr: '0.2rem', mt: '0.5rem' }} // height needs to be set otherwise the icon-buttons become oval
            >
              <IconButton onClick={() => handleDeleteFormField(fieldObj)}>
                <DeleteRounded />
              </IconButton>
              <IconButton onClick={e => handleOpenFieldObjMenu(e, fieldObj)}>
                <EditRounded />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              label={fieldObj.label}
              required={fieldObj.required}
              multiline
              rows={4}
            />
          </Box>
        )
      case 'sectionTitle':
        return (
          <Box display="flex" alignItems="center" ref={setFieldRef(fieldObj.key)}>
            <Stack direction="row">
              <IconButton onClick={() => handleDeleteFormField(fieldObj)}>
                <DeleteRounded />
              </IconButton>
              <IconButton onClick={e => handleOpenFieldObjMenu(e, fieldObj)}>
                <EditRounded />
              </IconButton>
            </Stack>
            <Typography variant="h6" fontWeight={600}>
              {fieldObj.label}
            </Typography>
          </Box>
        )
    }
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */

  /* ------------------------- Pre render actions end ------------------------- */

  useEffect(() => {
    if (pendingAnchorKey !== null) {
      const anchor = fieldRefs.current[pendingAnchorKey]
      if (anchor) {
        setFormFieldMenuAnchor(anchor)
        setOpenFieldObjMenu(true)
        setPendingAnchorKey(null)
      }
    }
  }, [pendingAnchorKey, formJson.formFields])

  useEffect(() => {
    if (openFieldObjMenu && labelInputRef.current) {
      log.debug('Nerv nicht')
      labelInputRef.current.focus()
      // TODO: the auto select is triggerd with every keystroke which makes it impossible
      // to enter labal names longer than one character. Check why this useEffect
      // is run on every keystroke, instead of only when the menu opens.
      // labelInputRef.current.select()
      // labelInputRef.current.setSelectionRange(0, labelInputRef.current.value.length)
    }
  }, [openFieldObjMenu, selectedFormObj])

  log.debug('formJson ->', formJson)
  const duplicateLabelsExist = hasDuplicateLabels(formJson.formFields)
  const hasEmptyFieldLabels = hasEmptyLabels(formJson.formFields)
  const selectedFormObjHasDuplicateLabel = isEditableField(selectedFormObj)
    ? isDuplicateLabel(selectedFormObj.label, selectedFormObj.key)
    : false
  const labelPlaceholder = getLabelPlaceholder(formJson.formFields)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        onClose={handleClose}
        title={t('common:content.heading.createFeedbackForm')}
        size="big"
        confirmButtonProps={{
          text: t('common:interaction.button.createFeedbackForm'),
          onConfirm: () => void (async () => await handleConfirm())(),
          disabled:
            formJson.formTitle.trim() === '' ||
            formJson.formFields.length <= 1 ||
            hasEmptyFieldLabels ||
            duplicateLabelsExist,
        }}
      >
        <Box padding="16px;" sx={{ border: '1px solid lightGrey', borderRadius: '4px' }}>
          <TextField
            fullWidth
            size="small"
            label={t('common:content.label.feedbackFormTitle')}
            inputProps={{
              style: {
                fontSize: '1.5rem',
                lineHeight: '1.334rem',
                fontWeight: 700,
                textAlign: 'center',
              },
            }} // style the input to be the size of an h5 heading
            value={formJson.formTitle}
            onChange={e => setFormJson((prev: any) => ({ ...prev, formTitle: e.target.value }))}
            error={formJson.formTitle === ''}
            sx={{ mb: '2.5rem' }}
          />
          <Grid container spacing={2}>
            {formJson.formFields.map((formFieldJson: any, index: number) => {
              return (
                <Grid item xs={12} key={formFieldJson.key ?? index}>
                  {renderEditorFieldObject(formFieldJson)}
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </MultilevelDrawer>
      <Popover
        anchorEl={formFieldMenuAnchor}
        open={openFieldObjMenu}
        onClose={handleCloseFieldObjMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        elevation={2}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ padding: '8px' }}>
          {selectedFormObj?.type !== 'sectionTitle' && (
            <FormControl size="small">
              <InputLabel id="select-formField-input-length">
                {t('common:content.label.inputLength')}
              </InputLabel>
              <Select
                label={t('common:content.label.inputLength')}
                labelId="select-formField-input-length"
                sx={{ width: '8rem' }}
                value={selectedFormObj?.type ?? 'text'}
                onChange={handleChangeFieldObjInputLength}
              >
                <MenuItem value={'text'}>{t('common:content.label.short')}</MenuItem>
                <MenuItem value={'multiline'}>{t('common:content.label.long')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <TextField
            label={t('common:content.label.fieldLabel')}
            value={selectedFormObj?.label ?? ''}
            onChange={handleChangeFieldObjLabel}
            size="small"
            error={selectedFormObj?.label === '' || selectedFormObjHasDuplicateLabel}
            required
            placeholder={labelPlaceholder}
            inputRef={labelInputRef}
            helperText={
              selectedFormObjHasDuplicateLabel
                ? t('common:feedback.error.duplicateFieldLabel')
                : selectedFormObj?.label === ''
                  ? t('common:feedback.error.missingFieldLabel')
                  : undefined
            }
          />
          {selectedFormObj?.type !== 'sectionTitle' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedFormObj?.required ?? false}
                  onChange={handleChangeFieldObjIsRequired}
                />
              }
              label={t('common:content.label.requiredField')}
            />
          )}
        </Stack>
      </Popover>
    </>
  )
}
